import {
  createSlice,
  createSelector,
  PayloadAction,
  current,
} from '@reduxjs/toolkit';
import { AppState } from '@store/store';
import { generateUniqueId } from '@utils/utilities';
import deepmerge from 'deepmerge';

export type MediaState = {
  id: string;
  blob: null | string;
  size: number;
  type: string;
  name: string;
};
export interface ToolEditorState {
  media: MediaState;
  config: ToolConfigBlock;
  currentBlock: BlockType | null;
}

const initialToolEditorState: ToolEditorState = {
  media: {
    id: '',
    blob: null,
    size: 0,
    type: '',
    name: '',
  },
  currentBlock: null,
  config: {
    id: '',
    finalDuration: 0,
    postProcessing: {
      output_format: '',
      upload_bucket: 'invideo-uploads-ap-south-1',
      compress: {
        quality: 85,
      },
    },
    blocks: [],
  },
};

function insertItem(array: Array<any>, index: number, item: any) {
  return [...array.slice(0, index), item, ...array.slice(index)];
}

const EditorSlice = createSlice({
  name: 'editor',
  initialState: initialToolEditorState,
  reducers: {
    ADD_VIDEO_BLOB: (
      state,
      action: PayloadAction<{
        blob: null | string;
        size: number;
        type: string;
        name: string;
      }>,
    ) => {
      return {
        ...state,
        media: { id: generateUniqueId(), ...action.payload },
      };
    },
    SELECT_CURRENT_BLOCK: (state, action: PayloadAction<string>) => {
      const selectedBlock = state.config.blocks.find(
        block => block.id === action.payload,
      );

      if (selectedBlock) {
        return {
          ...state,
          currentBlock: selectedBlock,
        };
      }

      return state;
    },
    ADD_BLOCK: (
      state,
      action: PayloadAction<{ block: BaseBlockType; position?: number }>,
    ) => {
      const block: BlockType = {
        ...action.payload.block,
        id: generateUniqueId(),
        block_id: generateUniqueId(),
        block_order: state.config.blocks.length + 1,
      };

      const curerntState = current(state);

      let newBlocks = curerntState.config.blocks;

      if (action.payload.position) {
        newBlocks = insertItem(newBlocks, action.payload.position, block).map(
          (blk, index) => ({ ...blk, block_order: index + 1 }),
        );
      } else {
        newBlocks = [...newBlocks, block];
      }

      const updateState = {
        ...state,
        config: {
          ...state.config,
          id: generateUniqueId(),
          blocks: newBlocks,
        },
      };

      updateState.currentBlock = block;

      return updateState;
    },
    UPDATE_BLOCK: (state, action: PayloadAction<BlockType>) => {
      const updatedBlock = state.config.blocks.map(block => {
        if (block.block_order === action.payload.block_order) {
          return deepmerge(block, action.payload);
        }
        return block;
      });

      let currentBlock = state.currentBlock;

      if (currentBlock && action.payload.id === state.currentBlock?.id) {
        currentBlock = deepmerge(currentBlock, action.payload);
      }

      return {
        ...state,
        currentBlock: currentBlock,
        config: {
          ...state.config,
          blocks: updatedBlock,
        },
      };
    },
    DELETE_BLOCK: (state, action: PayloadAction<number>) => {
      if (state.config.blocks.length === 1) {
        return state;
      }
      const selectedBlockIndex = state.config.blocks.findIndex(
        block => block.block_order === action.payload,
      );

      const updatedState = {
        ...state,
        config: {
          ...state.config,
          id: generateUniqueId(),
          blocks: state.config.blocks
            .filter(block => block.block_order !== action.payload)
            .map((block, i) => ({ ...block, block_order: i + 1 })),
        },
      };

      const updateSelectedBlock =
        selectedBlockIndex === 0
          ? updatedState.config.blocks[selectedBlockIndex + 1]
          : updatedState.config.blocks[selectedBlockIndex - 1];

      updatedState.currentBlock = updateSelectedBlock;
      return updatedState;
    },
    SET_OUTPUT_FORMAT: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        config: {
          ...state.config,
          postProcessing: {
            ...state.config.postProcessing,
            output_format: action.payload,
          },
        },
      };
    },
    RESET_EDITOR_STATE: () => {
      return { ...initialToolEditorState };
    },
  },
});

export const {
  ADD_VIDEO_BLOB,
  UPDATE_BLOCK,
  ADD_BLOCK,
  DELETE_BLOCK,
  SELECT_CURRENT_BLOCK,
  RESET_EDITOR_STATE,
  SET_OUTPUT_FORMAT,
} = EditorSlice.actions;

export const videoMetaSelector = createSelector(
  (state: AppState) => state.editor,
  state => state.media,
);

export const blockSelector = createSelector(
  (state: AppState) => state.editor,
  state => state.config.blocks,
);

export const configIdSelector = createSelector(
  (state: AppState) => state.editor,
  state => state.config.id,
);

export const postProcessingSelector = createSelector(
  (state: AppState) => state.editor,
  state => state.config.postProcessing,
);

export const configSelector = createSelector(
  (state: AppState) => state.editor,
  state => state.config,
);

export const currentSelectedBlock = createSelector(
  (state: AppState) => state.editor,
  state => state.currentBlock,
);

export default EditorSlice.reducer;
